import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  Alert, Autocomplete, Box, Button, Chip, DialogActions, DialogContent, DialogTitle, FormControlLabel, IconButton, MenuItem, Paper, Radio, RadioGroup,
  Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import { AddRounded, CloseRounded, NotificationsActiveOutlined } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  FOLLOW_UP_MODES, LEAD_HOME_KEY, LEAD_OPTIONS_KEY, LEADS_KEY, OPEN_STAGES, SALES_LIST_KEY, SALES_LIST_ONE, STAGE_LABEL, leadsApi, salesListsApi,
  type AgeGroup, type CustomerBase, type FollowUpMode, type Lead, type LeadStage, type SalesListKind, type TravellerIn,
} from '@/api/crm.api';
import { useAuth } from '@/auth/useAuth';
import ResponsiveDialog from '@/components/common/ResponsiveDialog';
import { useNotify } from '@/components/common/useNotify';
import { DestinationTags } from '@/components/vendors/VendorBits';
import { QuickAddDestinationDialog } from '@/components/places/DestinationSelect';
import { ApiError } from '@/lib/apiClient';

/** Anyone who sees leads (own, team or all). */
export const LEAD_VIEW = ['Lead.View', 'Lead.ViewTeam', 'Lead.ViewAll'];

const STAGE_SX: Record<LeadStage, { bg: string; fg: string }> = {
  New: { bg: '#E6EEF9', fg: '#1D4E89' }, Contacted: { bg: '#E7F1FB', fg: '#155E9C' }, QuoteSent: { bg: '#FDF1E3', fg: '#9A5B13' },
  FollowUp: { bg: '#FFF4D6', fg: '#8A6100' }, Converted: { bg: '#E7F4EC', fg: '#1E6B3A' }, Suspended: { bg: '#EEF0F3', fg: '#4B5563' },
  Cancelled: { bg: '#FCEFEF', fg: '#B42318' },
};

export function StageChip({ stage }: { stage: LeadStage }) {
  const s = STAGE_SX[stage];
  return <Chip size="small" label={STAGE_LABEL[stage]} sx={{ bgcolor: s.bg, color: s.fg, fontWeight: 600, height: 22 }} />;
}

const GROUP_SX: Record<AgeGroup, { bg: string; fg: string }> = {
  Adult: { bg: '#E6EEF9', fg: '#1D4E89' }, Child: { bg: '#E7F4EC', fg: '#1E6B3A' }, Infant: { bg: '#EEF0F3', fg: '#4B5563' },
};

export function AgeGroupChip({ group }: { group: AgeGroup }) {
  return <Chip size="small" label={group} sx={{ bgcolor: GROUP_SX[group].bg, color: GROUP_SX[group].fg, fontWeight: 600, height: 20 }} />;
}

/** FR-CRM-12: infant under 2, child 2 to under 12, adult 12 and above. */
export const groupOf = (age: number): AgeGroup => (age < 2 ? 'Infant' : age < 12 ? 'Child' : 'Adult');

/** "Said "child", counted as adult (12 and above)", as the server writes it. */
export const ageNote = (age: number | null | undefined, saidChild: boolean) =>
  age == null ? null : age >= 12 && age < 18 ? (saidChild ? 'Said "child", counted as adult (12 and above)' : `Aged ${age}: counted as adult (12 and above)`) : null;

/** "Today 16:30", "Tomorrow 10:00", "Overdue · today 12:00", "20 Nov, 10:00". */
export function dueLabel(at?: string | null, overdue?: boolean) {
  if (!at) return '—';
  const d = dayjs(at);
  const day = d.isSame(dayjs(), 'day') ? 'today' : d.isSame(dayjs().add(1, 'day'), 'day') ? 'tomorrow' : d.isSame(dayjs().subtract(1, 'day'), 'day') ? 'yesterday' : d.format('D MMM');
  const text = `${day} ${d.format('HH:mm')}`;
  return overdue ? `Overdue · ${text}` : text.charAt(0).toUpperCase() + text.slice(1);
}

/** The stage path (L6): New → Contacted → Quote sent → Follow-up; a click moves an open lead (ending always opens End lead). */
export function StagePath({ stage, onMove, disabled }: { stage: LeadStage; onMove?: (s: LeadStage) => void; disabled?: boolean }) {
  const at = OPEN_STAGES.indexOf(stage);
  return (
    <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap" useFlexGap>
      {OPEN_STAGES.map((s, i) => {
        const on = s === stage;
        const past = at >= 0 && i < at;
        return (
          <Stack key={s} direction="row" alignItems="center" spacing={0.75}>
            {i > 0 && <Typography color="text.secondary" fontSize={13}>›</Typography>}
            <Chip size="small" label={STAGE_LABEL[s]} clickable={!!onMove && !disabled && !on && at >= 0} onClick={onMove && !disabled && at >= 0 && !on ? () => onMove(s) : undefined}
              sx={{ fontWeight: 600, height: 22, bgcolor: on ? STAGE_SX[s].bg : past ? '#E7F4EC' : 'transparent', color: on ? STAGE_SX[s].fg : past ? '#1E6B3A' : 'text.secondary',
                border: on || past ? 0 : '1px solid', borderColor: 'divider' }} />
          </Stack>
        );
      })}
      {at < 0 && <><Typography color="text.secondary" fontSize={13}>›</Typography><StageChip stage={stage} /></>}
    </Stack>
  );
}

export function useLeadOptions(enabled = true) {
  return useQuery({ queryKey: [LEAD_OPTIONS_KEY], queryFn: leadsApi.options, staleTime: 120_000, enabled });
}

// ---------------------------------------------------------------- the lists behind the dropdowns, with "+ Add new" (FR-UI-05)

export function useSalesList(kind: SalesListKind, enabled = true) {
  return useQuery({ queryKey: [SALES_LIST_KEY, kind], queryFn: () => salesListsApi.list(kind), staleTime: 120_000, enabled });
}

/** One value of a sales list (by id), ending with "+ Add new". */
export function SalesListSelect({ kind, value, onChange, label, required, error, helperText, disabled, filter }: {
  kind: SalesListKind; value: number | null | undefined; onChange: (id: number | null) => void; label?: string; required?: boolean; error?: boolean;
  helperText?: ReactNode; disabled?: boolean; filter?: (base?: string | null) => boolean;
}) {
  const list = useSalesList(kind);
  const [adding, setAdding] = useState<string | null>(null);
  const [typed, setTyped] = useState('');
  const rows = (list.data ?? []).filter((i) => (i.isActive || i.id === value) && (!filter || filter(i.base)));
  const ADD = -1;
  return (
    <>
      <Autocomplete size="small" options={[...rows.map((r) => r.id), ADD]} value={value ?? null} disabled={disabled} loading={list.isLoading}
        getOptionLabel={(o) => (o === ADD ? `+ Add new ${SALES_LIST_ONE[kind]}` : rows.find((r) => r.id === o)?.name ?? '')}
        onInputChange={(_, v, reason) => { if (reason === 'input') setTyped(v); }}
        onChange={(_, v) => { if (v === ADD) setAdding(typed); else onChange(v); }}
        renderOption={(props, o) => {
          const { key, ...rest } = props as typeof props & { key: string };
          return <li key={key} {...rest} style={o === ADD ? { color: '#1D4E89', fontWeight: 600, borderTop: '1px solid #E5E9F0' } : undefined}>{o === ADD ? '+ Add new' : rows.find((r) => r.id === o)?.name}</li>;
        }}
        renderInput={(p) => <TextField {...p} label={label} required={required} error={error} helperText={helperText} />} />
      {adding !== null && <QuickAddSalesItem kind={kind} initialName={adding} onClose={() => setAdding(null)} onAdded={(id) => { setAdding(null); onChange(id); }} />}
    </>
  );
}

/** Several values of a sales list (special preferences), as chips, ending with "+ Add new". */
export function SalesListMulti({ kind, value, onChange, label, placeholder }: {
  kind: SalesListKind; value: number[]; onChange: (ids: number[]) => void; label?: string; placeholder?: string;
}) {
  const list = useSalesList(kind);
  const [adding, setAdding] = useState<string | null>(null);
  const [typed, setTyped] = useState('');
  const rows = (list.data ?? []).filter((i) => i.isActive || value.includes(i.id));
  const ADD = -1;
  return (
    <>
      <Autocomplete multiple size="small" options={[...rows.map((r) => r.id), ADD]} value={value} filterSelectedOptions loading={list.isLoading}
        getOptionLabel={(o) => (o === ADD ? '+ Add new' : rows.find((r) => r.id === o)?.name ?? '')}
        onInputChange={(_, v, reason) => { if (reason === 'input') setTyped(v); }}
        onChange={(_, v) => { if (v.includes(ADD)) { setAdding(typed); return; } onChange(v); }}
        renderOption={(props, o) => {
          const { key, ...rest } = props as typeof props & { key: string };
          return <li key={key} {...rest} style={o === ADD ? { color: '#1D4E89', fontWeight: 600, borderTop: '1px solid #E5E9F0' } : undefined}>{o === ADD ? '+ Add new' : rows.find((r) => r.id === o)?.name}</li>;
        }}
        renderTags={(v, getTagProps) => v.map((id, i) => { const { key, ...rest } = getTagProps({ index: i }); return <Chip key={key} {...rest} size="small" label={rows.find((r) => r.id === id)?.name} sx={{ bgcolor: '#E6EEF9', color: '#1D4E89', fontWeight: 600 }} />; })}
        renderInput={(p) => <TextField {...p} label={label} placeholder={value.length ? '' : placeholder} />} />
      {adding !== null && <QuickAddSalesItem kind={kind} initialName={adding} onClose={() => setAdding(null)} onAdded={(id) => { setAdding(null); onChange([...value, id]); }} />}
    </>
  );
}

/** "+ Add new" pop-up for a sales list: an existing name is refused and offered; a near spelling asks to confirm (FR-UI-06). */
export function QuickAddSalesItem({ kind, initialName, onClose, onAdded }: { kind: SalesListKind; initialName?: string; onClose: () => void; onAdded: (id: number) => void }) {
  const qc = useQueryClient();
  const notify = useNotify();
  const list = useSalesList(kind);
  const [name, setName] = useState(initialName ?? '');
  const [base, setBase] = useState<CustomerBase>('Traveller');
  const [near, setNear] = useState<string | null>(null);
  const same = (list.data ?? []).find((i) => i.name.trim().replace(/\s+/g, ' ').toUpperCase() === name.trim().replace(/\s+/g, ' ').toUpperCase());
  const add = useMutation({ mutationFn: (confirmNear: boolean) => salesListsApi.add(kind, { name: name.trim(), base: kind === 'CustomerType' ? base : null, confirmNear }) });
  const go = async (confirmNear: boolean) => {
    try {
      const row = await add.mutateAsync(confirmNear);
      await qc.invalidateQueries({ queryKey: [SALES_LIST_KEY, kind] });
      qc.invalidateQueries({ queryKey: [LEAD_OPTIONS_KEY] });
      notify.success(`'${row.name}' added.`);
      onAdded(row.id);
    } catch (e) {
      if (e instanceof ApiError && e.errorCode === 'NEAR_SPELLING') setNear(e.message);
      else notify.error(e);
    }
  };
  return (
    <ResponsiveDialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Add new {SALES_LIST_ONE[kind]}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField size="small" autoFocus label="Name" required value={name} onChange={(e) => { setName(e.target.value); setNear(null); }} error={!!same}
            helperText={same ? `'${same.name}' already exists, so it cannot be added again. Select the existing one.` : ' '} />
          {same && <Button variant="outlined" size="small" onClick={() => onAdded(same.id)}>Select '{same.name}'</Button>}
          {kind === 'CustomerType' && (
            <TextField select size="small" label="Fields it has" value={base} onChange={(e) => setBase(e.target.value as CustomerBase)}
              helperText="Travel agents and corporates also have company name, GSTIN, contact person and credit.">
              <MenuItem value="Traveller">Like a traveller (B2C)</MenuItem><MenuItem value="TravelAgent">Like a travel agent (B2B)</MenuItem><MenuItem value="Corporate">Like a corporate</MenuItem>
            </TextField>
          )}
          {near && <Alert severity="warning">{near}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button color="inherit" onClick={onClose}>Cancel</Button>
        {near
          ? <Button variant="contained" color="warning" disabled={add.isPending} onClick={() => go(true)}>Yes, it is different: add it</Button>
          : <Button variant="contained" disabled={!name.trim() || !!same || add.isPending} onClick={() => go(false)}>Add and select</Button>}
      </DialogActions>
    </ResponsiveDialog>
  );
}

/** Lead destinations: chips of the company's destinations, with "+ Add new" for a place not in Destinations yet. */
export function DestinationsField({ value, onChange, error, helperText }: { value: number[]; onChange: (ids: number[]) => void; error?: boolean; helperText?: ReactNode }) {
  const qc = useQueryClient();
  const { hasAnyPermission } = useAuth();
  const [adding, setAdding] = useState(false);
  return (
    <Box>
      <DestinationTags value={value} onChange={onChange} error={error} helperText={helperText} placeholder="Type a destination" />
      {hasAnyPermission(['City.Create']) && <Button size="small" startIcon={<AddRounded />} sx={{ mt: 0.25, px: 0.5 }} onClick={() => setAdding(true)}>Add new destination</Button>}
      {adding && <QuickAddDestinationDialog initialName="" onClose={() => setAdding(false)}
        onPicked={(id) => { setAdding(false); qc.invalidateQueries({ queryKey: ['destination-options'] }); onChange([...value.filter((v) => v !== id), id]); }} />}
    </Box>
  );
}

// ---------------------------------------------------------------- travellers by age (L5, L10)

/** One row per traveller with age; the group is shown as it is typed (infant under 2, child 2 to under 12, adult 12+). */
export function TravellersEditor({ rows, onChange, error }: { rows: TravellerIn[]; onChange: (rows: TravellerIn[]) => void; error?: string | null }) {
  const set = (i: number, p: Partial<TravellerIn>) => onChange(rows.map((r, j) => (j === i ? { ...r, ...p } : r)));
  return (
    <Box>
      <Table size="small">
        <TableHead><TableRow><TableCell sx={{ width: 32 }}>#</TableCell><TableCell sx={{ width: 90 }}>Age</TableCell><TableCell>Counted as</TableCell><TableCell sx={{ width: 40 }} /></TableRow></TableHead>
        <TableBody>
          {rows.map((r, i) => {
            const note = ageNote(r.age, !!r.saidChild);
            return (
              <TableRow key={i}>
                <TableCell>{i + 1}</TableCell>
                <TableCell>
                  <TextField size="small" type="number" value={r.age ?? ''} error={!!error && r.age == null} inputProps={{ min: 0, max: 120, 'aria-label': `Age of traveller ${i + 1}` }}
                    onChange={(e) => set(i, { age: e.target.value === '' ? null : Math.max(0, Math.min(120, Number(e.target.value))) })} sx={{ width: 76 }} />
                </TableCell>
                <TableCell>
                  {r.age == null ? <Typography variant="caption" color="error">Age needed</Typography> : (
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                      <AgeGroupChip group={groupOf(r.age)} />
                      {r.age >= 12 && r.age < 18 && (
                        <FormControlLabel sx={{ ml: 0 }} control={<input type="checkbox" checked={!!r.saidChild} onChange={(e) => set(i, { saidChild: e.target.checked })} style={{ marginRight: 6 }} />}
                          label={<Typography variant="caption">Said "child"</Typography>} />
                      )}
                      {note && <Typography variant="caption" color="warning.dark">{note}</Typography>}
                    </Stack>
                  )}
                </TableCell>
                <TableCell>
                  <Tooltip title="Delete"><span><IconButton size="small" color="error" disabled={rows.length === 1} onClick={() => onChange(rows.filter((_, j) => j !== i))}><CloseRounded fontSize="small" /></IconButton></span></Tooltip>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <Button size="small" startIcon={<AddRounded />} sx={{ mt: 1 }} onClick={() => onChange([...rows, { age: null }])}>Add traveller</Button>
      {error && <Typography variant="caption" color="error" component="div">{error}</Typography>}
      <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
        Infant under 2 · child 2 to under 12 · adult 12 and above. Each hotel's own child policy decides the hotel charge in the quote.
      </Typography>
    </Box>
  );
}

// ---------------------------------------------------------------- L7 Add follow-up

const toLocalInput = (d: dayjs.Dayjs) => d.format('YYYY-MM-DDTHH:mm');

/** L7: done on (now, can be changed), how, what the customer said, next follow-up date and time (required until the lead ends). */
export function FollowUpDialog({ lead, onClose, onSaved, onEndInstead }: { lead: Lead; onClose: () => void; onSaved: (l: Lead) => void; onEndInstead: () => void }) {
  const notify = useNotify();
  const [doneOn, setDoneOn] = useState(toLocalInput(dayjs()));
  const [mode, setMode] = useState<FollowUpMode>('Call');
  const [said, setSaid] = useState('');
  const [next, setNext] = useState('');
  const [tried, setTried] = useState(false);
  const save = useMutation({
    mutationFn: () => leadsApi.followUp(lead.id, { doneAt: dayjs(doneOn).toISOString(), mode, customerSaid: said.trim(), nextAt: next ? dayjs(next).toISOString() : null }),
  });
  const problems = [
    !said.trim() && 'What the customer said is mandatory. Please fill it in.',
    !next && 'Next follow-up date and time are mandatory until the lead ends.',
    next && dayjs(next).isBefore(dayjs()) && 'Next follow-up must be a date and time from now on.',
  ].filter(Boolean) as string[];
  return (
    <ResponsiveDialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add follow-up · {lead.code} {lead.customer.name}</DialogTitle>
      <DialogContent>
        {tried && problems.length > 0 && <Alert severity="error" sx={{ mb: 2 }}>{problems.map((p) => <div key={p}>{p}</div>)}</Alert>}
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField size="small" type="datetime-local" label="Done on" required value={doneOn} onChange={(e) => setDoneOn(e.target.value)} helperText="Now, can be changed"
              slotProps={{ inputLabel: { shrink: true } }} sx={{ flex: 1 }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary">How *</Typography>
              <RadioGroup row value={mode} onChange={(e) => setMode(e.target.value as FollowUpMode)}>
                {FOLLOW_UP_MODES.map((m) => <FormControlLabel key={m} value={m} control={<Radio size="small" />} label={m} />)}
              </RadioGroup>
            </Box>
          </Stack>
          <TextField size="small" label="What the customer said" required multiline minRows={2} value={said} onChange={(e) => setSaid(e.target.value)}
            error={tried && !said.trim()} inputProps={{ maxLength: 2000 }} />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField size="small" type="datetime-local" label="Next follow-up" required value={next} onChange={(e) => setNext(e.target.value)}
              error={tried && (!next || dayjs(next).isBefore(dayjs()))} helperText="Date and time are required until the lead ends"
              slotProps={{ inputLabel: { shrink: true } }} sx={{ flex: 1 }} />
            <TextField size="small" label="Remind" value={lead.reminderText.replace(/^reminder /, '')} disabled sx={{ flex: 1 }} />
          </Stack>
          <Typography variant="caption" color="text.secondary">
            Customer ready to decide? <Box component="span" sx={{ color: 'primary.main', cursor: 'pointer', fontWeight: 600 }} onClick={onEndInstead}>Use End lead instead</Box> (Converted, Suspended or Cancelled).
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button color="inherit" onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={save.isPending} onClick={async () => {
          setTried(true);
          if (problems.length) return;
          try { const l = await save.mutateAsync(); notify.success('Follow-up saved.'); onSaved(l); } catch (e) { notify.error(e); }
        }}>Save follow-up</Button>
      </DialogActions>
    </ResponsiveDialog>
  );
}

// ---------------------------------------------------------------- L9 End lead

/** L9: Converted (needs an accepted quote), Suspended (reopen date, note), Cancelled (reason from the list, note). */
export function EndLeadDialog({ lead, initial, onClose, onEnded }: { lead: Pick<Lead, 'id' | 'code' | 'customer'>; initial?: LeadStage; onClose: () => void; onEnded: (l: Lead) => void }) {
  const notify = useNotify();
  const [outcome, setOutcome] = useState<LeadStage>(initial && ['Converted', 'Suspended', 'Cancelled'].includes(initial) ? initial : 'Suspended');
  const [reopen, setReopen] = useState('');
  const [reasonId, setReasonId] = useState<number | null>(null);
  const [note, setNote] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const end = useMutation({ mutationFn: () => leadsApi.end(lead.id, { outcome, reopenOn: outcome === 'Suspended' ? reopen : null, reasonId: outcome === 'Cancelled' ? reasonId : null, note: note.trim() || null }) });
  const box = (o: LeadStage, title: string, body: ReactNode) => (
    <Paper variant="outlined" onClick={() => setOutcome(o)} sx={{ p: 1.5, flex: 1, cursor: 'pointer', borderColor: outcome === o ? 'primary.main' : 'divider', borderWidth: outcome === o ? 2 : 1 }}>
      <FormControlLabel value={o} control={<Radio size="small" checked={outcome === o} />} label={<Typography fontWeight={600}>{title}</Typography>} />
      <Box sx={{ mt: 1 }}>{body}</Box>
    </Paper>
  );
  return (
    <ResponsiveDialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>End lead · {lead.code} {lead.customer.name}<Typography variant="body2" color="text.secondary">Choose how this lead ends.</Typography></DialogTitle>
      <DialogContent>
        {problem && <Alert severity="error" sx={{ mb: 2 }}>{problem}</Alert>}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
          {box('Converted', 'Converted', <Typography variant="body2" color="text.secondary">Needs an accepted quote. Goes on to booking and invoice.</Typography>)}
          {box('Suspended', 'Suspended', (
            <Stack spacing={1.25}>
              <TextField size="small" type="date" label="Reopen on" required value={reopen} onChange={(e) => setReopen(e.target.value)} disabled={outcome !== 'Suspended'}
                slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: dayjs().add(1, 'day').format('YYYY-MM-DD') } }}
                helperText="Comes back as a follow-up on this date, with reminders" />
              <TextField size="small" label="Note" value={outcome === 'Suspended' ? note : ''} onChange={(e) => setNote(e.target.value)} disabled={outcome !== 'Suspended'} />
            </Stack>
          ))}
          {box('Cancelled', 'Cancelled', (
            <Stack spacing={1.25}>
              <SalesListSelect kind="CancelReason" label="Reason" required value={reasonId} onChange={setReasonId} disabled={outcome !== 'Cancelled'} />
              <TextField size="small" label="Note" value={outcome === 'Cancelled' ? note : ''} onChange={(e) => setNote(e.target.value)} disabled={outcome !== 'Cancelled'} />
            </Stack>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button color="inherit" onClick={onClose}>Cancel</Button>
        <Button variant="contained" color={outcome === 'Cancelled' ? 'error' : 'primary'} disabled={end.isPending} onClick={async () => {
          if (outcome === 'Suspended' && !reopen) { setProblem('Reopen on is mandatory for a suspended lead.'); return; }
          if (outcome === 'Cancelled' && !reasonId) { setProblem('Reason is mandatory to cancel a lead.'); return; }
          setProblem(null);
          try { const l = await end.mutateAsync(); notify.success(`${lead.code} ${outcome.toLowerCase()}.`); onEnded(l); } catch (e) { setProblem(e instanceof Error ? e.message : 'The lead could not be ended.'); }
        }}>End lead</Button>
      </DialogActions>
    </ResponsiveDialog>
  );
}

// ---------------------------------------------------------------- L8 reminder pop-up (on every screen)

/**
 * L8: "Follow-up in 15 minutes" pop-up, asked every minute; Dismiss or Open lead. The same reminder also goes to the mobile
 * app, WhatsApp and email from the server.
 */
export function ReminderPopups() {
  const { hasAnyPermission } = useAuth();
  const can = hasAnyPermission(LEAD_VIEW);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['crm-popups'], queryFn: leadsApi.popups, enabled: can, refetchInterval: 60_000, refetchIntervalInBackground: true });
  const [closed, setClosed] = useState<number[]>([]);
  const shown = useRef<Set<number>>(new Set());
  const items = (q.data ?? []).filter((p) => !closed.includes(p.id));
  useEffect(() => {
    // A new reminder refreshes Home so its follow-up shows Due.
    if (items.some((p) => !shown.current.has(p.id))) { items.forEach((p) => shown.current.add(p.id)); qc.invalidateQueries({ queryKey: [LEAD_HOME_KEY] }); }
  }, [items, qc]);
  if (!can || items.length === 0) return null;
  const dismiss = async (id: number) => { setClosed((c) => [...c, id]); try { await leadsApi.dismiss(id); } catch { /* shown again next minute */ } };
  return (
    <Stack spacing={1} sx={{ position: 'fixed', right: 16, bottom: { xs: 88, md: 24 }, zIndex: (t) => t.zIndex.snackbar, width: 340, maxWidth: 'calc(100vw - 32px)' }}>
      {items.slice(0, 3).map((p) => (
        <Paper key={p.id} elevation={6} sx={{ p: 2, borderLeft: 4, borderColor: 'primary.main' }} role="alert">
          <Stack direction="row" spacing={1} alignItems="center"><NotificationsActiveOutlined color="primary" fontSize="small" />
            <Typography fontWeight={700} fontSize={14}>Follow-up in {Math.max(0, dayjs(p.dueAt).diff(dayjs(), 'minute'))} minutes</Typography></Stack>
          <Typography variant="body2" sx={{ mt: 0.5 }}><b>{p.customer}</b> · {p.leadCode} · {p.mode} at {dayjs(p.dueAt).format('HH:mm')}</Typography>
          {p.lastNote && <Typography variant="caption" color="text.secondary" component="div">Last note: "{p.lastNote}"</Typography>}
          <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1 }}>
            <Button size="small" color="inherit" onClick={() => dismiss(p.id)}>Dismiss</Button>
            <Button size="small" variant="contained" onClick={() => { void dismiss(p.id); navigate(`/leads/${p.leadId}`); }}>Open lead</Button>
          </Stack>
        </Paper>
      ))}
    </Stack>
  );
}

/** The Enquiries badge: my follow-ups due today and overdue. */
export function useEnquiriesBadge() {
  const { hasAnyPermission } = useAuth();
  const can = hasAnyPermission(LEAD_VIEW);
  const q = useQuery({ queryKey: [LEAD_HOME_KEY], queryFn: leadsApi.home, enabled: can, refetchInterval: 120_000, staleTime: 60_000 });
  return can ? q.data?.today.length ?? 0 : 0;
}

/** Reassign one or more leads to a salesperson (manager, admin). */
export function ReassignDialog({ ids, onClose, onDone }: { ids: number[]; onClose: () => void; onDone: () => void }) {
  const notify = useNotify();
  const qc = useQueryClient();
  const opts = useLeadOptions();
  const [to, setTo] = useState<number | null>(null);
  const run = useMutation({ mutationFn: () => leadsApi.reassign(ids, to!) });
  const people = useMemo(() => opts.data?.assignees ?? [], [opts.data]);
  return (
    <ResponsiveDialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Reassign {ids.length === 1 ? 'lead' : `${ids.length} leads`}</DialogTitle>
      <DialogContent>
        <TextField select fullWidth size="small" label="Assign to" value={to ?? ''} onChange={(e) => setTo(Number(e.target.value))} sx={{ mt: 1 }}>
          {people.map((p) => <MenuItem key={p.id} value={p.id}>{p.short} · {p.name}</MenuItem>)}
        </TextField>
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>The new salesperson is told by email; reminders move to them.</Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button color="inherit" onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!to || run.isPending} onClick={async () => {
          try { notify.success(await run.mutateAsync()); qc.invalidateQueries({ queryKey: [LEADS_KEY] }); onDone(); } catch (e) { notify.error(e); }
        }}>{ids.length === 1 ? 'Yes, reassign' : `Yes, reassign ${ids.length}`}</Button>
      </DialogActions>
    </ResponsiveDialog>
  );
}
